import React, { useEffect, useState } from 'react';
import { useNavigation } from 'react-router-dom';
import { useForgeTheme } from '../../forge/hooks/useForgeTheme';
import './route-navigation-progress.css';

/**
 * How long a navigation has to stay pending before the bar appears. Under a
 * warm cache a route chunk resolves in a few milliseconds, and flashing a bar
 * for one frame reads as a glitch; a slow chunk on a cold cache is well past
 * this.
 */
export const NAVIGATION_FEEDBACK_DELAY_MS = 150;

/** The visible track. Kept separate so the live region can outlive it. */
const ProgressTrack: React.FC = () => (
  <>
    <span className="route-navigation-progress__track" data-testid="route-navigation-progress">
      <span className="route-navigation-progress__bar" />
    </span>
    <span className="route-navigation-progress__label">Loading page…</span>
  </>
);

/**
 * Data-router navigation feedback.
 *
 * Every page route in the app is now `route.lazy`, and the data router *waits*
 * on that chunk before it swaps the element in — so without this the UI is
 * simply frozen on the old page while a slow chunk downloads, with no signal
 * that the click registered. `useNavigation().state` is the router's own view
 * of that pending window.
 *
 * The `role="status"` wrapper stays mounted whether or not a navigation is in
 * flight: screen readers announce mutations *inside* an existing live region,
 * so a region that appears together with its text is usually never spoken. Only
 * the track and the label mount and unmount.
 *
 * Each shell renders this, rather than one copy at the router root, because the
 * theme tokens only resolve inside a `.forge-root[data-forge-theme]` scope and
 * the shells are what establish it.
 */
export const RouteNavigationProgress: React.FC = () => {
  const navigation = useNavigation();
  const isNavigating = navigation.state !== 'idle';
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isNavigating) {
      setIsVisible(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setIsVisible(true), NAVIGATION_FEEDBACK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isNavigating]);

  return (
    <div
      className="route-navigation-progress"
      // `aria-live` without `role="status"` on purpose: the region has to stay mounted
      // for its label to be announced, and `role="status"` would put a second status
      // landmark on every page, colliding with the pages' own status regions.
      aria-live="polite"
      data-pending={isVisible ? 'true' : 'false'}
      data-testid="route-navigation-progress-region"
    >
      {isVisible ? <ProgressTrack /> : null}
    </div>
  );
};

/**
 * Shown while the data router resolves the `lazy` chunks for the *initial*
 * match — a cold deep link straight to a lazy route.
 *
 * Without this react-router renders `null` for that whole window (and warns
 * `No HydrateFallback element provided to render during initial hydration`),
 * so a direct link to a Control/Eval/Archon page painted a blank white document
 * until its chunk arrived. `RouteNavigationProgress` cannot cover that window:
 * it lives inside the shells, and the shells are exactly what the router is
 * still withholding. This one establishes its own token scope for the same
 * reason `AppErrorElement` does.
 */
export const RouteHydrateFallback: React.FC = () => {
  const { theme } = useForgeTheme();

  return (
    <div
      className="forge-root route-hydrate-fallback"
      data-forge-theme={theme}
      data-testid="route-hydrate-fallback"
    >
      <div className="route-navigation-progress" aria-live="polite" data-pending="true">
        <ProgressTrack />
      </div>
    </div>
  );
};
